import React, { useState } from 'react';
import { TimberItem } from '../../types/bidding';
import { updateTimberItemBoardFeet } from '../../utils/timberListParser';
import './ManualTimberEntry.css';

interface ManualTimberEntryProps {
  items: TimberItem[];
  onUpdate: (items: TimberItem[]) => void;
}

interface NewItemForm {
  name: string;
  quantity: number;
  width: number;
  height: number;
  orderLength: string;
  material: string;
}

const ManualTimberEntry: React.FC<ManualTimberEntryProps> = ({ items, onUpdate }) => {
  const [newItem, setNewItem] = useState<NewItemForm>({
    name: '',
    quantity: 1,
    width: 0,
    height: 0,
    orderLength: '',
    material: '#1 GRN DF',
  });

  const handleAddItem = () => {
    if (!newItem.name || newItem.width <= 0 || newItem.height <= 0 || !newItem.orderLength) {
      alert('Please fill in all required fields');
      return;
    }

    const timberItem: TimberItem = {
      id: `manual-${Date.now()}-${Math.random()}`,
      name: newItem.name,
      material: newItem.material,
      quantity: newItem.quantity,
      width: newItem.width,
      height: newItem.height,
      realLength: newItem.orderLength, // Same as order length for manual entry
      orderLength: newItem.orderLength,
      boardFeet: 0,
      unitPrice: 0,
      totalPrice: 0,
      manuallyEdited: true,
    };

    const itemWithBoardFeet = updateTimberItemBoardFeet(timberItem);
    onUpdate([...items, itemWithBoardFeet]);

    // Reset form
    setNewItem({
      name: '',
      quantity: 1,
      width: 0,
      height: 0,
      orderLength: '',
      material: '#1 GRN DF',
    });
  };

  const handleRemoveItem = (index: number) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index: number, field: keyof TimberItem, value: any) => {
    const updatedItems = items.map((item, i) => {
      if (i === index) {
        const updated = { ...item, [field]: value };
        return updateTimberItemBoardFeet(updated);
      }
      return item;
    });
    onUpdate(updatedItems);
  };

  return (
    <div className="manual-timber-entry">
      <div className="entry-header">
        <h3>Manual Timber List Entry</h3>
        <p className="entry-subtitle">Add timber items one at a time. Pricing will be fetched when you proceed.</p>
      </div>

      {/* Add New Item Form */}
      <div className="add-item-form">
        <div className="form-row">
          <div className="form-field">
            <label>Item Name *</label>
            <input
              type="text"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              placeholder="e.g., Post, Beam, Rafter"
            />
          </div>

          <div className="form-field form-field-small">
            <label>Qty *</label>
            <input
              type="number"
              min="1"
              value={newItem.quantity || ''}
              onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
            />
          </div>

          <div className="form-field form-field-small">
            <label>Width (in) *</label>
            <input
              type="number"
              step="0.25"
              min="0"
              value={newItem.width || ''}
              onChange={(e) => setNewItem({ ...newItem, width: parseFloat(e.target.value) || 0 })}
              placeholder="e.g., 8"
            />
          </div>

          <div className="form-field form-field-small">
            <label>Height (in) *</label>
            <input
              type="number"
              step="0.25"
              min="0"
              value={newItem.height || ''}
              onChange={(e) => setNewItem({ ...newItem, height: parseFloat(e.target.value) || 0 })}
              placeholder="e.g., 8"
            />
          </div>

          <div className="form-field">
            <label>Length (in) *</label>
            <input
              type="text"
              value={newItem.orderLength}
              onChange={(e) => setNewItem({ ...newItem, orderLength: e.target.value })}
              placeholder="e.g., 96, 8', 12'-6&quot;"
            />
          </div>

          <div className="form-field">
            <label>Material *</label>
            <select
              value={newItem.material}
              onChange={(e) => setNewItem({ ...newItem, material: e.target.value })}
            >
              <option value="#1 GRN DF">#1 GRN DF (Green Douglas Fir)</option>
              <option value="#2 GRN DF">#2 GRN DF</option>
              <option value="#1 KD DF">#1 KD DF (Kiln Dried)</option>
              <option value="#2 KD DF">#2 KD DF</option>
              <option value="#1 GRN WRC">#1 GRN WRC (Western Red Cedar)</option>
              <option value="#2 GRN WRC">#2 GRN WRC</option>
              <option value="#1 GRN HF">#1 GRN HF (Hemlock Fir)</option>
              <option value="#2 GRN HF">#2 GRN HF</option>
              <option value="SELECT GRN SPF">SELECT GRN SPF (Spruce Pine Fir)</option>
              <option value="CONST GRN SPF">CONST GRN SPF</option>
              <option value="#1 PT SYP">#1 PT SYP (Pressure Treated Southern Yellow Pine)</option>
              <option value="#2 PT SYP">#2 PT SYP</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <button className="add-item-btn" onClick={handleAddItem}>
            + Add Item
          </button>
        </div>
      </div>

      {/* Items List */}
      {items.length > 0 ? (
        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Qty</th>
                <th>Width</th>
                <th>Height</th>
                <th>Length</th>
                <th>Board Feet</th>
                <th>Material</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleUpdateItem(index, 'name', e.target.value)}
                      className="inline-input"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="inline-input inline-input-small"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.25"
                      value={item.width}
                      onChange={(e) => handleUpdateItem(index, 'width', parseFloat(e.target.value) || 0)}
                      className="inline-input inline-input-small"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.25"
                      value={item.height}
                      onChange={(e) => handleUpdateItem(index, 'height', parseFloat(e.target.value) || 0)}
                      className="inline-input inline-input-small"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.orderLength}
                      onChange={(e) => handleUpdateItem(index, 'orderLength', e.target.value)}
                      className="inline-input inline-input-small"
                    />
                  </td>
                  <td className="board-feet-cell">
                    {(item.boardFeet * item.quantity).toFixed(2)} BF
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.material}
                      onChange={(e) => handleUpdateItem(index, 'material', e.target.value)}
                      className="inline-input"
                    />
                  </td>
                  <td>
                    <button
                      className="remove-item-btn"
                      onClick={() => handleRemoveItem(index)}
                      title="Remove item"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="items-summary">
            <strong>Total Items:</strong> {items.length} | 
            <strong> Total Board Feet:</strong> {items.reduce((sum, item) => sum + (item.boardFeet * item.quantity), 0).toFixed(2)} BF
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <p>No items added yet. Use the form above to add your first timber item.</p>
        </div>
      )}
    </div>
  );
};

export default ManualTimberEntry;
